'use client';

import * as React from 'react';
import {
  ColumnDef,
  ColumnFiltersState,
  SortingState,
  VisibilityState,
  flexRender,
  getCoreRowModel,
  getFilteredRowModel,
  getSortedRowModel,
  useReactTable,
} from '@tanstack/react-table';

import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { useMemo, useState } from 'react';
import { useTranslations } from 'next-intl';
import { useInvites } from '@/hooks/swr/use-invites';
import { Invite } from '@prisma/client';
import { DeleteInvitePopover } from '@/components/popover/DeleteInvitePopover';
import Avatar from '@/components/misc/Avatar';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { toast } from '@/hooks/use-toast';
import { useSession } from 'next-auth/react';
import { useScheduleUsers } from '@/hooks/swr/use-schedule-users';

type ColumnMeta = {
  className?: string;
};

function useColumns({
  isOwner,
  update,
}: {
  isOwner: boolean;
  update: (id: string, role: 'OWNER' | 'MEMBER' | 'MANAGER_RESOURCE' | 'MANAGER_PROJECT') => Promise<unknown>;
}) {
  const t = useTranslations('calendar');
  
  return useMemo<ColumnDef<Invite, unknown>[]>(() => {
    return [
      {
        accessorKey: 'email',
        header: t('fields.email'),
        meta: {
          className: 'w-80',
        },
        cell: ({ row }) => {
          return (
            <div className="flex items-center gap-3">
              <Avatar name={row.original.email} />
              <span>{row.original.email}</span>
            </div>
          );
        },
      },
      {
        accessorKey: 'role',
        header: t('fields.role'),
        cell: ({ row }) => {
          const invite = row.original;
          return (
            <Select
              value={invite.role}
              disabled={!isOwner}
              onValueChange={async (newRole) => {
                try {
                  await update(invite.id, newRole as 'OWNER' | 'MEMBER' | 'MANAGER_RESOURCE' | 'MANAGER_PROJECT');
                  toast({
                    title: t('dialog.invite.roleUpdated'),
                  });
                } catch (error) {
                  toast({
                    title: t('dialog.invite.roleUpdateFailed'),
                    variant: 'destructive',
                    description: error instanceof Error ? error.message : undefined,
                  });
                }
              }}
            >
              <SelectTrigger className="w-[180px]">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {['OWNER', 'MEMBER', 'MANAGER_RESOURCE', 'MANAGER_PROJECT'].map((r) => (
                  <SelectItem key={r} value={r}>
                    {t('roles.' + r)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          );
        },
      },
      {
        id: 'actions',
        enableHiding: false,
        enableSorting: false,
        cell: ({ row }) => {
          return (
            <div className="text-right">
              <DeleteInvitePopover id={row.original.id} scheduleId={row.original.scheduleId} />
            </div>
          );
        },
      },
    ];
  }, [t, isOwner, update]);
}

export function InviteTable({ scheduleId }: { scheduleId: string }) {
  const { invites, update } = useInvites({ scheduleId });
  const { scheduleUsers } = useScheduleUsers({ scheduleId });
  const { data: session } = useSession();
  const isOwner = scheduleUsers.find((u) => u.userId === session?.user.id)?.role === 'OWNER';
  const [sorting, setSorting] = useState<SortingState>([{ id: 'email', desc: false }]);
  const columns = useColumns({ isOwner, update });
  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([]);
  const [columnVisibility, setColumnVisibility] = useState<VisibilityState>({
    categoryId: false,
  });
  const [rowSelection, setRowSelection] = useState({});

  const table = useReactTable({
    data: invites,
    columns,
    onSortingChange: setSorting,
    onColumnFiltersChange: setColumnFilters,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    onColumnVisibilityChange: setColumnVisibility,
    onRowSelectionChange: setRowSelection,
    state: {
      sorting,
      columnFilters,
      columnVisibility,
      rowSelection,
    },
  });

  if (!invites.length) {
    return;
  }

  return (
    <div>
      <Table className="table-fixed">
        <TableHeader>
          {table.getHeaderGroups().map((headerGroup) => (
            <TableRow key={headerGroup.id}>
              {headerGroup.headers.map((header) => {
                // Get className from meta if it exists
                const className =
                  (header.column.columnDef.meta as ColumnMeta | undefined)?.className || '';
                return (
                  <TableHead key={header.id} className={className}>
                    {header.isPlaceholder
                      ? null
                      : flexRender(header.column.columnDef.header, header.getContext())}
                  </TableHead>
                );
              })}
            </TableRow>
          ))}
        </TableHeader>
        <TableBody>
          {table.getRowModel().rows?.length ? (
            table.getRowModel().rows.map((row) => (
              <TableRow
                key={row.id}
                data-state={row.getIsSelected() && 'selected'}
                className="h-10"
              >
                {row.getVisibleCells().map((cell) => {
                  // Get className from meta if it exists
                  const className =
                    (cell.column.columnDef.meta as ColumnMeta | undefined)?.className || '';
                  return (
                    <TableCell key={cell.id} className={className}>
                      {flexRender(cell.column.columnDef.cell, cell.getContext())}
                    </TableCell>
                  );
                })}
              </TableRow>
            ))
          ) : (
            <TableRow>
              <TableCell colSpan={columns.length}></TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
    </div>
  );
}
